import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react'
import styled from 'styled-components'
import InboxControlBar from './ControlBar/InboxControlBar'
import Inbox from './Inbox'
import KeyboardShortcutsModal from './Modals/KeyboardShortcutsModal'
import TrainAiModal from './Modals/TrainAiModal'
import InboxModals from './InboxModals'
import { useInboxKeyboard } from './useInboxKeyboard'
import { useAuthStore } from '../../../../stores/auth/useAuthStore'
import { useAuthActions } from '../../../../stores/auth/useAuthActions'
import { useInboxHandlers } from '../../../../services/actions/inbox/useInboxHandlers'
import { canShowSalesNavigatorTab, evaluateSalesNavigatorGuard, SALES_NAVIGATOR_GUARD_MESSAGE } from '../../../../services/inbox/salesNavigatorAccess'
import { useAppHandlers } from '../../../../services/actions/app/useAppHandlers'
import { useAssistantRequest } from '../../../../services/apis/assistant/useAssistantRequest'
import { shouldAutoOpenTrainAiModal } from './trainAiModalPolicy'
import { useInboxStore } from '../../../../stores/inbox/useInboxStore'
import { useInboxSettingsStore } from '../../../../stores/inbox/useInboxSettingsStore'
import { useAppStore } from '../../../../stores/application/useAppStore'
import { useNavigate } from 'react-router-dom'
import { AppRoutes } from '../../../../interfaces/store/appStore'
import { ChevronDown, SlidersHorizontal } from 'lucide-react'
import TooltipWrapper from '../../../../components/TooltipWrapper'

const TRAIN_AI_MODAL_AUTO_OPEN_DELAY_MS = 2000

type ConversationTagTabId =
    | 'mainInbox'
    | 'allMessages'
    | 'awaitingReply'
    | 'ai:To respond'
    | 'snoozed'
    | 'newConnection'
    | 'ai:Warm intro'
    | 'salesNav'
    | 'ai:FYI'
    | 'ai:Actioned'
    | 'ai:Marketing'

const SCOPE_CONVERSATION_TAG_TABS: { id: ConversationTagTabId; label: string }[] = [
    { id: 'mainInbox', label: 'Main inbox' },
    { id: 'allMessages', label: 'All messages' },
]

const REST_PRIMARY_CONVERSATION_TAG_TABS: { id: ConversationTagTabId; label: string }[] = [
    { id: 'awaitingReply', label: 'Awaiting reply' },
    { id: 'ai:To respond', label: 'To Respond' },
    { id: 'snoozed', label: 'Snoozed' },
    { id: 'newConnection', label: 'New Connection' },
    { id: 'ai:Warm intro', label: 'Warm Intro' },
    { id: 'salesNav', label: 'Sales Nav' },
]

const MORE_CONVERSATION_TAG_TABS: { id: ConversationTagTabId; label: string }[] = [
    { id: 'ai:FYI', label: 'FYI' },
    { id: 'ai:Actioned', label: 'Actioned' },
    { id: 'ai:Marketing', label: 'Marketing' },
]

const InboxConversationTagTabs: React.FC = () => {
    const [moreOpen, setMoreOpen] = useState(false)
    const moreRef = useRef<HTMLDivElement>(null)
    const { store: { selectedTab, activeInboxLabel, salesNavigatorData }, dispatch } = useInboxStore()
    const { store: { defaultView } } = useInboxSettingsStore()
    const { store: { user } } = useAuthStore()
    const { store: { extensionInstalled } } = useAppStore()
    const { openInboxSourceTabHandler, loadSalesNavigatorUser, isSharedInbox } = useInboxHandlers()
    const navigate = useNavigate()

    useEffect(() => {
        loadSalesNavigatorUser()
    }, [extensionInstalled, user?._id, user?.linkedinData?.publicIdentifier])

    /** Default view in Settings controls which scope tab appears first in the bar. */
    const primaryConversationTagTabs = useMemo(() => {
        const scopeTabs =
            defaultView === 'all' ? [...SCOPE_CONVERSATION_TAG_TABS].reverse() : SCOPE_CONVERSATION_TAG_TABS
        return [...scopeTabs, ...REST_PRIMARY_CONVERSATION_TAG_TABS]
    }, [defaultView])

    /** Separator sits after the second scope tab (Main inbox / All messages pair). */
    const scopeSeparatorAfterId =
        defaultView === 'all' ? 'mainInbox' : 'allMessages'

    const activeTab = selectedTab === 'SalesNavigator' ? 'salesNav' : activeInboxLabel
    // Shared inbox: Sales Navigator threads come from the proxy accounts — the user's own SN plan is irrelevant.
    const salesNavTabVisible = isSharedInbox || canShowSalesNavigatorTab(user?.linkedinData, salesNavigatorData)
    const salesNavGuard = isSharedInbox ? 'ok' : evaluateSalesNavigatorGuard(user?.linkedinData, salesNavigatorData)
    const salesNavDisabled = salesNavGuard !== 'ok'
    const salesNavTooltip = salesNavGuard === 'ok' ? '' : SALES_NAVIGATOR_GUARD_MESSAGE[salesNavGuard]

    const selectConversationTagTab = (id: ConversationTagTabId) => {
        if (id === 'salesNav') {
            if (salesNavDisabled) return
            if (selectedTab !== 'SalesNavigator') openInboxSourceTabHandler('SalesNavigator')
            dispatch({ type: 'set-active-inbox-label', payload: 'salesNav' })
            setMoreOpen(false)
            return
        }

        if (selectedTab === 'SalesNavigator') openInboxSourceTabHandler('Inbox')
        dispatch({ type: 'set-active-inbox-label', payload: id })
        setMoreOpen(false)
    }

    const moreActive = MORE_CONVERSATION_TAG_TABS.some(tab => tab.id === activeTab)

    const openSettings = () => {
        setMoreOpen(false)
        navigate(AppRoutes.SETTINGS_CONVERSATION_TAGS)
    }

    useEffect(() => {
        if (!moreOpen) return
        const handleOutsideClick = (event: MouseEvent) => {
            if (moreRef.current && !moreRef.current.contains(event.target as Node)) setMoreOpen(false)
        }
        document.addEventListener('mousedown', handleOutsideClick)
        return () => document.removeEventListener('mousedown', handleOutsideClick)
    }, [moreOpen])

    return (
        <ConversationTagTabsBar>
            <ConversationTagTabsScroll role="tablist" aria-label="Inbox conversation tag filters">
                {primaryConversationTagTabs.map(tab => {
                    if (tab.id === 'salesNav' && !salesNavTabVisible) return null
                    return (
                    <React.Fragment key={tab.id}>
                        <TooltipWrapper
                            tooltipText={tab.id === 'salesNav' && salesNavDisabled ? salesNavTooltip : ''}
                            placement="top"
                        >
                            <LabelTabButton
                                type="button"
                                role="tab"
                                aria-selected={activeTab === tab.id}
                                disabled={tab.id === 'salesNav' && salesNavDisabled}
                                $active={activeTab === tab.id}
                                onClick={() => selectConversationTagTab(tab.id)}
                            >
                                {tab.label}
                            </LabelTabButton>
                        </TooltipWrapper>
                        {tab.id === scopeSeparatorAfterId && <LabelTabSeparator aria-hidden="true" />}
                    </React.Fragment>
                    )
                })}
                <MoreTabsWrapper ref={moreRef}>
                    <LabelTabButton
                        type="button"
                        role="tab"
                        aria-selected={moreActive}
                        $active={moreActive}
                        onClick={() => setMoreOpen(open => !open)}
                    >
                        More
                        <MoreChevron size={14} aria-hidden="true" focusable="false" />
                    </LabelTabButton>
                    {moreOpen && (
                        <MoreTabsMenu>
                            {MORE_CONVERSATION_TAG_TABS.map(tab => (
                                <MoreTabsItem
                                    key={tab.id}
                                    type="button"
                                    $active={activeTab === tab.id}
                                    onClick={() => selectConversationTagTab(tab.id)}
                                >
                                    {tab.label}
                                </MoreTabsItem>
                            ))}
                            <MoreTabsDivider />
                            <MoreTabsItem
                                type="button"
                                $active={false}
                                onClick={openSettings}
                            >
                                <SettingsIcon size={16} aria-hidden="true" focusable="false" />
                                Settings
                            </MoreTabsItem>
                        </MoreTabsMenu>
                    )}
                </MoreTabsWrapper>
            </ConversationTagTabsScroll>
        </ConversationTagTabsBar>
    )
}

const InboxPage: React.FC = () => {
    const [, setFiltersHeight] = useState(0)
    const [shortcutsOpen, setShortcutsOpen] = useState(false)
    const [trainAiModalOpen, setTrainAiModalOpen] = useState(false)
    const [isTrainingAi, setIsTrainingAi] = useState(false)
    const [canStartToolbarOnboarding, setCanStartToolbarOnboarding] = useState(false)
    const dismissedTrainAiModalThisVisitRef = useRef(false)
    const trainAiAutoOpenTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    const { store: { user } } = useAuthStore()
    const userRef = useRef(user)
    userRef.current = user
    const { setUser } = useAuthActions()
    const { trainAiConversationsHandler, isSharedInbox } = useInboxHandlers()
    const { showSuccessToast, showErrorToast } = useAppHandlers()
    const { recordTrainAiInboxModalSkip } = useAssistantRequest()

    const openShortcuts = useCallback(() => setShortcutsOpen(true), [])
    const closeShortcuts = useCallback(() => setShortcutsOpen(false), [])

    useInboxKeyboard(openShortcuts)

    const clearTrainAiAutoOpenTimer = useCallback(() => {
        if (trainAiAutoOpenTimerRef.current != null) {
            clearTimeout(trainAiAutoOpenTimerRef.current)
            trainAiAutoOpenTimerRef.current = null
        }
    }, [])

    useEffect(() => {
        if (!user?._id || dismissedTrainAiModalThisVisitRef.current) return

        clearTrainAiAutoOpenTimer()

        // Training reads the user's own conversations through the extension — not offered in the shared inbox.
        if (isSharedInbox || !shouldAutoOpenTrainAiModal(user)) {
            setCanStartToolbarOnboarding(true)
            return
        }

        trainAiAutoOpenTimerRef.current = setTimeout(() => {
            trainAiAutoOpenTimerRef.current = null
            if (dismissedTrainAiModalThisVisitRef.current) return
            setTrainAiModalOpen(true)
        }, TRAIN_AI_MODAL_AUTO_OPEN_DELAY_MS)

        return () => {
            clearTrainAiAutoOpenTimer()
        }
    }, [user, user?._id, user?.conversationsRecordedForAi, user?.trainAiInboxModalSkippedAt, clearTrainAiAutoOpenTimer, isSharedInbox])

    const closeTrainAiModal = () => {
        clearTrainAiAutoOpenTimer()
        dismissedTrainAiModalThisVisitRef.current = true
        setTrainAiModalOpen(false)
        setCanStartToolbarOnboarding(true)
    }

    const handleTrainAiSkip = async () => {
        try {
            const data: any = await recordTrainAiInboxModalSkip()
            if (data?.trainAiInboxModalSkippedAt && user) {
                setUser({ ...user, trainAiInboxModalSkippedAt: data.trainAiInboxModalSkippedAt })
            }
        } catch {
            showErrorToast({ message: 'Could not save preference.' })
        } finally {
            closeTrainAiModal()
        }
    }

    const openTrainAiModal = () => {
        clearTrainAiAutoOpenTimer()
        setTrainAiModalOpen(true)
    }

    return (
        <Container>
            <InboxControlBar
                setFiltersHeight={setFiltersHeight}
                onShowShortcuts={openShortcuts}
                onOpenTrainAiModal={openTrainAiModal}
                isTrainingAi={isTrainingAi}
                allowToolbarOnboarding={canStartToolbarOnboarding}
            />
            <InboxConversationTagTabs />
            <ContentArea>
                <Inbox />
            </ContentArea>
            {shortcutsOpen && <KeyboardShortcutsModal onClose={closeShortcuts} />}
            <InboxModals />
            {trainAiModalOpen && (
                <TrainAiModal
                    onClose={closeTrainAiModal}
                    onSkip={handleTrainAiSkip}
                    onConfirm={async (opts) => {
                        clearTrainAiAutoOpenTimer()
                        setTrainAiModalOpen(false)
                        dismissedTrainAiModalThisVisitRef.current = true
                        setCanStartToolbarOnboarding(true)
                        setIsTrainingAi(true)
                        try {
                            const res = await trainAiConversationsHandler(opts)
                            if (res.success) {
                                showSuccessToast({ message: res.message })
                                const u = userRef.current
                                if (u) {
                                    setUser({
                                        ...u,
                                        conversationsRecordedForAi: true,
                                        conversationsRecordedForAiAt: new Date().toISOString(),
                                    })
                                }
                            } else {
                                showErrorToast({ message: res.message })
                            }
                        } catch {
                            showErrorToast({ message: 'Failed to train AI with conversations.' })
                        } finally {
                            setIsTrainingAi(false)
                        }
                    }}
                />
            )}
        </Container>
    )
}

const Container = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
    overflow: hidden;
`
const ContentArea = styled.div`
    flex: 1;
    min-height: 0;
    display: flex;
`
const ConversationTagTabsBar = styled.div`
    width: 100%;
    flex-shrink: 0;
    background: #fafafa;
    border-bottom: 1px solid #e8eaed;
    padding: 8px 12px;
`
const ConversationTagTabsScroll = styled.div`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
    overflow: visible;
`
const LabelTabSeparator = styled.div`
    width: 1px;
    height: 20px;
    flex-shrink: 0;
    align-self: center;
    background: #e8eaed;
    margin: 0 2px;
`
const LabelTabButton = styled.button<{ $active: boolean }>`
    display: flex;
    height: 32px;
    flex-shrink: 0;
    align-items: center;
    gap: 4px;
    border: 0;
    border-radius: 9999px;
    background: ${({ $active }) => ($active ? '#f0efff' : 'transparent')};
    padding: 0 12px;
    font-family: 'Nunito Sans', sans-serif;
    font-size: 12px;
    line-height: 16px;
    font-weight: 500;
    color: ${({ $active }) => ($active ? '#6467f2' : '#6a7181')};
    cursor: pointer;
    transition: all 0.15s ease;
    white-space: nowrap;

    &:hover:not(:disabled) {
        background: ${({ $active }) => ($active ? '#f0efff' : '#f5f6fa')};
        color: ${({ $active }) => ($active ? '#6467f2' : '#41495e')};
    }

    &:disabled {
        cursor: not-allowed;
        opacity: 0.45;
    }
`
const MoreTabsWrapper = styled.div`
    position: relative;
    flex-shrink: 0;
`
const MoreChevron = styled(ChevronDown)`
    width: 14px;
    height: 14px;
    flex-shrink: 0;
`
const MoreTabsMenu = styled.div`
    position: absolute;
    top: calc(100% + 6px);
    right: 0;
    z-index: 20;
    min-width: 170px;
    padding: 6px;
    border: 1px solid #e8eaed;
    border-radius: 10px;
    background: #ffffff;
    box-shadow: 0 12px 30px rgba(51, 57, 81, 0.12);
`
const MoreTabsItem = styled.button<{ $active: boolean }>`
    width: 100%;
    height: 32px;
    border: 0;
    border-radius: 8px;
    background: ${({ $active }) => ($active ? '#f0efff' : 'transparent')};
    color: ${({ $active }) => ($active ? '#6467f2' : '#41495e')};
    cursor: pointer;
    font-family: 'Nunito Sans', sans-serif;
    font-size: 13px;
    font-weight: 600;
    text-align: left;
    padding: 0 10px;
    display: flex;
    align-items: center;
    gap: 8px;

    &:hover {
        background: ${({ $active }) => ($active ? '#f0efff' : '#f5f6fa')};
    }
`
const MoreTabsDivider = styled.div`
    height: 1px;
    background: #e8eaed;
    margin: 6px -6px;
`
const SettingsIcon = styled(SlidersHorizontal)`
    width: 16px;
    height: 16px;
    flex-shrink: 0;
    color: #6467f2;
`

export default InboxPage
